import { HttpErrorResponse } from '@angular/common/http';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	ElementRef,
	inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { type ComparatorBill, ComparatorBillSchema } from '@libs/dtos/comparator/comparator.dto';
import {
	PUBLIC_CONTRACT_FILE_MAX_BYTES,
	PUBLIC_CONTRACT_FILE_TYPES,
	PUBLIC_CONTRACT_MAX_FILES,
	type PublicComparisonResponseDTO,
	type PublicContractKind,
	type PublicOfferCardDTO,
} from '@libs/dtos/comparator/comparator-public.dto';
import { BILL_READER_MAX_BYTES, BILL_READER_MIME_TYPES } from '@libs/dtos/comparator/comparator-reader.dto';
import { checkBill } from '@libs/utils/bill-comparator';
import { WebComparatorService } from '@services/public/web-comparator.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { FileDropzoneComponent } from '@shared/components/file-dropzone/file-dropzone.component';
import { TARIFF_LABELS } from '@shared/constants/domain.const';
import { SharedModule } from '@shared/modules/shared.module';
import { flattenTransparentImage } from '@shared/utils/flatten-image';
import { formatEuros, formatNumber } from '@shared/utils/format';
import { TurnstileWidget } from './turnstile';

type Step = 'upload' | 'result' | 'contract' | 'done';

/**
 * Public comparator for the ERIS website (GK-1059), shown in an iframe from
 * `/web/comparador`. A visitor uploads a bill, sees one card per marketer with
 * the savings, and «Contratar» sends a request to the ERIS approval tray. The
 * bill is never stored; it is only read and compared.
 */
@Component({
	selector: 'app-web-comparator',
	standalone: true,
	imports: [SharedModule, ButtonComponent, FileDropzoneComponent],
	templateUrl: './web-comparator.component.html',
	styleUrls: ['./web-comparator.component.scss'],
})
export class WebComparatorComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('turnstileBox') turnstileBox?: ElementRef<HTMLElement>;

	private readonly service = inject(WebComparatorService);
	private readonly translocoService = inject(TranslocoService);
	private readonly fb = inject(FormBuilder);
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly destroyRef = inject(DestroyRef);
	private readonly turnstile = new TurnstileWidget(() => this.cdr.detectChanges());

	readonly accept = BILL_READER_MIME_TYPES.join(',');
	readonly documentAccept = PUBLIC_CONTRACT_FILE_TYPES.join(',');
	readonly kinds: PublicContractKind[] = ['INDIVIDUAL', 'SELF_EMPLOYED', 'COMPANY'];

	step: Step = 'upload';
	isLoadingConfig = true;
	closed = false;
	contractingEnabled = false;
	private siteKey: string | null = null;

	isReading = false;
	unreadable = false;
	error: string | null = null;

	bill: ComparatorBill | null = null;
	cupsRead: string | null = null;
	holderRead: string | null = null;
	result: PublicComparisonResponseDTO | null = null;
	selected: PublicOfferCardDTO | null = null;

	documents: File[] = [];
	isSending = false;

	readonly form = this.fb.nonNullable.group({
		kind: ['INDIVIDUAL' as PublicContractKind, Validators.required],
		name: ['', [Validators.required, Validators.maxLength(200)]],
		taxId: ['', [Validators.required, Validators.maxLength(20)]],
		representativeName: [''],
		representativeTaxId: [''],
		email: ['', [Validators.required, Validators.email]],
		phone: ['', Validators.required],
		iban: ['', Validators.required],
		cupsCode: ['', Validators.required],
		supplyAddress: [''],
		acceptPrivacy: [false, Validators.requiredTrue],
	});

	ngOnInit(): void {
		this.service
			.config()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (config) => {
					this.isLoadingConfig = false;
					this.siteKey = config.turnstileSiteKey;
					this.closed = config.turnstileSiteKey === null;
					this.contractingEnabled = config.contractingEnabled;
					this.cdr.detectChanges();
					this.renderTurnstile();
				},
				error: () => {
					this.isLoadingConfig = false;
					this.closed = true;
					this.cdr.detectChanges();
				},
			});
	}

	ngAfterViewInit(): void {
		this.renderTurnstile();
	}

	ngOnDestroy(): void {
		this.turnstile.destroy();
	}

	private rendered = false;
	private renderTurnstile(): void {
		if (this.rendered || !this.siteKey || !this.turnstileBox) return;
		this.rendered = true;
		this.turnstile.render(this.turnstileBox.nativeElement, this.siteKey).catch(() => {
			this.error = this.t('webComparator.errors.captcha');
			this.cdr.detectChanges();
		});
	}

	t(key: string, params?: Record<string, unknown>): string {
		return this.translocoService.translate(key, params);
	}

	euros(value: number): string {
		return formatEuros(value);
	}

	get isCompany(): boolean {
		return this.form.controls.kind.value === 'COMPANY';
	}

	get billSummary(): { label: string; value: string }[] {
		if (!this.bill) return [];
		const power = Object.entries(this.bill.contractedPowerKw)
			.map(([period, kw]) => `${period} ${formatNumber(kw)} kW`)
			.join(' · ');
		return [
			{
				label: this.t('webComparator.summary.tariff'),
				value: TARIFF_LABELS[this.bill.tariff] ?? this.bill.tariff,
			},
			{ label: this.t('webComparator.summary.power'), value: power },
			{
				label: this.t('webComparator.summary.consumption'),
				value: `${formatNumber(this.bill.totalConsumptionKwh)} kWh · ${this.bill.days} ${this.t('webComparator.summary.days')}`,
			},
			{ label: this.t('webComparator.summary.total'), value: formatEuros(this.bill.printed.total) },
		];
	}

	get savingCards(): PublicOfferCardDTO[] {
		return this.result?.cards.filter((card) => card.savings > 0) ?? [];
	}

	// ============================================
	// 1. Upload and read
	// ============================================

	async onFilesSelected(files: File[]): Promise<void> {
		const selected = files[0];
		if (!selected || this.isReading) return;
		this.error = null;
		this.unreadable = false;
		if (!(BILL_READER_MIME_TYPES as readonly string[]).includes(selected.type)) {
			this.error = this.t('webComparator.errors.billType');
			return;
		}
		this.isReading = true;
		this.cdr.detectChanges();
		const file = await flattenTransparentImage(selected);
		if (file.size > BILL_READER_MAX_BYTES) {
			this.isReading = false;
			this.error = this.t('webComparator.errors.billSize');
			this.cdr.detectChanges();
			return;
		}
		let token: string;
		try {
			token = await this.turnstile.take();
		} catch {
			this.isReading = false;
			this.error = this.t('webComparator.errors.captcha');
			this.cdr.detectChanges();
			return;
		}
		this.service
			.readBill(file, token)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (reading) => {
					const parsed = ComparatorBillSchema.safeParse(reading.draft);
					// Without a person to review it, only a reading that matches the printed total is shown.
					if (!parsed.success || !checkBill(parsed.data).ok) {
						this.isReading = false;
						this.unreadable = true;
						this.cdr.detectChanges();
						return;
					}
					this.bill = parsed.data;
					this.cupsRead = reading.identity.cups;
					this.holderRead = reading.identity.holderName;
					this.compare();
				},
				error: (error: HttpErrorResponse) => this.fail(error),
			});
	}

	private compare(): void {
		if (!this.bill) return;
		this.service
			.compare({ bill: this.bill })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (result) => {
					this.isReading = false;
					this.result = result;
					this.step = 'result';
					this.cdr.detectChanges();
				},
				error: (error: HttpErrorResponse) => this.fail(error),
			});
	}

	private fail(error: HttpErrorResponse): void {
		this.isReading = false;
		this.isSending = false;
		const message = typeof error?.error?.message === 'string' ? error.error.message : '';
		this.error = message || this.t('webComparator.errors.generic');
		this.cdr.detectChanges();
	}

	restart(): void {
		this.step = 'upload';
		this.bill = null;
		this.result = null;
		this.selected = null;
		this.unreadable = false;
		this.error = null;
		this.documents = [];
		this.form.reset();
		this.cdr.detectChanges();
	}

	// ============================================
	// 2. Contract
	// ============================================

	contract(card: PublicOfferCardDTO): void {
		this.selected = card;
		this.error = null;
		this.form.patchValue({ cupsCode: this.cupsRead ?? '', name: this.holderRead ?? '' });
		this.step = 'contract';
		this.cdr.detectChanges();
	}

	backToResult(): void {
		this.step = 'result';
		this.error = null;
		this.cdr.detectChanges();
	}

	onDocumentsSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const files = Array.from(input.files ?? []);
		input.value = '';
		this.error = null;
		const merged = [...this.documents, ...files];
		if (merged.length > PUBLIC_CONTRACT_MAX_FILES) {
			this.error = this.t('webComparator.errors.tooManyFiles');
			return;
		}
		for (const file of files) {
			if (!(PUBLIC_CONTRACT_FILE_TYPES as readonly string[]).includes(file.type)) {
				this.error = this.t('webComparator.errors.documentType');
				return;
			}
			if (file.size > PUBLIC_CONTRACT_FILE_MAX_BYTES) {
				this.error = this.t('webComparator.errors.documentSize');
				return;
			}
		}
		this.documents = merged;
	}

	removeDocument(index: number): void {
		this.documents = this.documents.filter((_, position) => position !== index);
	}

	async send(): Promise<void> {
		this.form.markAllAsTouched();
		const value = this.form.getRawValue();
		const companyMissing =
			this.isCompany && (!value.representativeName.trim() || !value.representativeTaxId.trim());
		if (this.form.invalid || companyMissing || this.documents.length === 0 || !this.bill || !this.selected) {
			this.error =
				this.documents.length === 0
					? this.t('webComparator.errors.noDocument')
					: this.t('webComparator.errors.form');
			return;
		}
		this.isSending = true;
		this.error = null;
		this.cdr.detectChanges();
		let token: string;
		try {
			token = await this.turnstile.take();
		} catch {
			this.isSending = false;
			this.error = this.t('webComparator.errors.captcha');
			this.cdr.detectChanges();
			return;
		}
		this.service
			.sendRequest(
				{
					kind: value.kind,
					name: value.name,
					taxId: value.taxId,
					representativeName: this.isCompany ? value.representativeName : null,
					representativeTaxId: this.isCompany ? value.representativeTaxId : null,
					email: value.email,
					phone: value.phone,
					iban: value.iban,
					cupsCode: value.cupsCode,
					supplyAddress: value.supplyAddress.trim() || null,
					offerId: this.selected.offerId,
					bill: this.bill,
					acceptPrivacy: true,
				},
				this.documents,
				token,
			)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.isSending = false;
					this.step = 'done';
					this.cdr.detectChanges();
				},
				error: (error: HttpErrorResponse) => this.fail(error),
			});
	}

	invalid(control: keyof typeof this.form.controls): boolean {
		const field = this.form.controls[control];
		return field.invalid && field.touched;
	}
}
